import React, { useContext, useEffect, useState } from "react";
import { IonCard, IonCol, IonRow, IonRadio, IonRadioGroup, IonCardHeader, IonCardSubtitle, IonCardTitle, IonCardContent, IonIcon, IonChip, IonButton } from "@ionic/react";
import { Barn, BarnMember, Contact, Event, EventEntry } from "../../../models";
import { trash } from "ionicons/icons";
import { BarnCardsSkeleton } from "../../Skeletons/EntrySectionSkeletons";
import SelectBarnFromAllBarns from "../../Barn/SelectBarnFromAllBarns";
import { formatPhoneNumberForDisplay } from "../../../utilities/contact/FormatPhoneNumber";
import { PersonContext } from "../../../context/PersonContext";
import { createBarnMember, deleteBarnMember, getBarnMembersByPersonId } from "../../../utilities/barnMember/BarnMember";
import { CreateBarnMemberInput } from "../../../API";
import { getAddressById } from "../../../utilities/address/Address";
import { getContactById } from "../../../utilities/contact/Contact";
import { formatAddress } from "../../../utilities/address/FormatAddress";
import ErrorBanner from "../../Banners/ErrorBanner";
import BasicModal from "../../Modals/BasicModal";
import EntryFormSectionHeader from "./EntryFormSectionHeader";
import { BARN_CARD_CONTENT_MIN_HEIGHT_PX } from "../../Skeletons/skeletonSizing";
import barnIcon from "../../../assets/img/icons/barn.png";

interface _Props {
    event: Event;
    currentEventEntry?: EventEntry;
    onSelect: Function;
    onDirtyChange?: (isDirty: boolean) => void;
}

interface FormattedBarnInfo {
    id: string;
    barn: Barn;
    addressString: string;
    contactString: string;
    websiteString?: string;
    barnMembership?: {
        id: string;
        permissionLevel: string | null;
    } | null;
}

const SelectBarnSection: React.FC<_Props> = ({ currentEventEntry, onSelect, onDirtyChange }) => {
    const user = useContext(PersonContext);

    const [selectedBarn, setSelectedBarn] = useState<Barn | null | undefined>(null);
    const [formattedBarns, setFormattedBarns] = useState<FormattedBarnInfo[]>([]);
    const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [modalBarn, setModalBarn] = useState<Barn | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>("");

    useEffect(() => {
        if (user) {
            fetchBarnMemberships();
        }
        if (currentEventEntry && currentEventEntry.barn) {
            setSelectedBarn(currentEventEntry.barn);
        }
    }, [currentEventEntry]);

    const fetchBarnMemberships = async (): Promise<void> => {
        setIsLoading(true);

        try {
            const barnMembershipsResult = await getBarnMembersByPersonId(user.id);

            if (barnMembershipsResult.isSuccess) {
                const barnMembers: BarnMember[] = barnMembershipsResult.result;

                // Alphabetize barnMembers by barn name with case insensitivity
                barnMembers.sort((a, b) => a.barn.name.localeCompare(b.barn.name, undefined, { sensitivity: "base" }));

                const barns = barnMembers.map((barnMember) => barnMember.barn);

                const formattedBarnsPromises = barnMembers.map(async (barnMember) => {
                    return await formatBarn(barnMember.barn, barnMember);
                });
                const formattedBarns = await Promise.all(formattedBarnsPromises);

                setFormattedBarns(formattedBarns);

                // Set the selected barn to the top if it exists
                const selectedBarn = currentEventEntry ? currentEventEntry.barn : barns[0];
                setSelectedBarn(selectedBarn);
                onSelect(selectedBarn);
                setIsLoading(false);
            } else {
                throw new Error("Failed to fetch barn memberships.");
            }
        } catch (error) {
            setError("Sorry, a problem occurred while finding barns that you're a member of.");
        } finally {
            setIsLoading(false);
        }
    };

    const formatBarn = async (barn: Barn, barnMember: BarnMember | null = null): Promise<FormattedBarnInfo> => {
        let addressString = "Address not provided",
            contactString = "Contact not provided",
            websiteString = "Website not provided";

        try {
            if (barn.barnLocationId) {
                const addressResult = await getAddressById(barn.barnLocationId);
                if (addressResult.isSuccess) {
                    const address = addressResult.result;
                    addressString = formatAddress(address);
                }
            }

            if (barn.barnContactId) {
                const contactResult = await getContactById(barn.barnContactId);
                if (contactResult.isSuccess) {
                    const contact: Contact = contactResult.result;
                    if (contact.work) contactString = formatPhoneNumberForDisplay(contact.work);
                    if (contact.website) websiteString = contact.website;
                }
            }
        } catch (error) {
            setError("An error occurred while formatting barn information.");
        }

        return {
            id: barn.id,
            barn,
            addressString,
            contactString,
            websiteString,
            barnMembership: barnMember
                ? {
                      id: barnMember.id,
                      permissionLevel: barnMember.permissionLevel || null,
                  }
                : null,
        };
    };

    const handleBarnSelection = async (barn: Barn | null | undefined) => {
        setIsLoading(true);

        if (!barn) {
            setIsLoading(false);
            onSelect(null);
            onDirtyChange?.(true);
            return;
        }

        try {
            // Check if the barn is already in the formattedBarns list
            const isBarnInList = formattedBarns.some((b) => b.barn.id === barn.id);

            if (!isBarnInList) {
                const formattedBarn = await formatBarn(barn);

                setFormattedBarns((prevBarns) => {
                    // Remove any existing entry for the barn (in case it exists from a previous selection)
                    const updatedBarns = prevBarns.filter((b) => b.barn.id !== barn.id);
                    // Add the new formatted barn to the top of the list
                    const newBarns = [formattedBarn, ...updatedBarns];
                    setIsLoading(false);
                    return newBarns;
                });
            } else {
                // If the barn is already in the list, move it to the top
                setFormattedBarns((prevBarns) => {
                    const updatedBarns = prevBarns.filter((b) => b.barn.id !== barn.id);
                    const newBarns = [formattedBarns.find((b) => b.barn.id === barn.id)!, ...updatedBarns];
                    setIsLoading(false);
                    return newBarns;
                });
            }

            setSelectedBarn(barn);
            onSelect(barn);
            onDirtyChange?.(true);
        } catch (error) {
            setError("An error occurred while handling barn selection.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleRadioButtonClick = (barn: Barn) => {
        setSelectedBarn(barn);
        onSelect(barn);
        onDirtyChange?.(true);
    };

    const openJoinModal = (barn: Barn) => {
        setModalBarn(barn);
        setIsJoinModalOpen(true);
    };

    const openDeleteModal = (barn: Barn) => {
        setModalBarn(barn);
        setIsDeleteModalOpen(true);
    };

    const closeJoinModal = () => setIsJoinModalOpen(false);
    const closeDeleteModal = () => setIsDeleteModalOpen(false);

    const addMembership = async () => {
        setIsLoading(true);

        if (!modalBarn) {
            setError("No barn found.");
            setIsLoading(false);
            return;
        }

        try {
            const newMemberInfo: CreateBarnMemberInput = {
                name: `${user.firstName} ${user.lastName}`,
                personId: user.id,
                barnId: modalBarn.id,
                permissionLevel: "member",
                roles: user.roles,
            };

            const { isSuccess, result } = await createBarnMember(newMemberInfo);

            if (isSuccess && result?.id) {
                // Update the specific barn's membership information with the new barnMembership Id
                setFormattedBarns((prevBarns) =>
                    prevBarns.map((barn) =>
                        barn.id === modalBarn.id
                            ? {
                                  ...barn,
                                  barnMembership: {
                                      id: result.id, // Use the barnMembership Id from the result
                                      permissionLevel: "member",
                                  },
                              }
                            : barn
                    )
                );
            } else {
                setError("Error: could not create the request.");
            }

            setIsJoinModalOpen(false);
        } catch (error) {
            setError("An error occurred while joining the barn.");
        } finally {
            setIsLoading(false);
        }
    };

    const deleteMembership = async () => {
        setIsLoading(true);

        if (!modalBarn) {
            setError("No barn found.");
            setIsLoading(false);
            return;
        }

        try {
            // Find the barnMember that matches the chosen barn
            const barnMember = formattedBarns.find((barn) => barn.id === modalBarn.id)?.barnMembership;

            if (!barnMember || !barnMember.id) {
                setError("No barn membership found.");
                setIsLoading(false);
                return;
            }

            const barnMembershipInfo = { id: barnMember.id };

            // Delete Barn membership using the provided deleteBarnMember function
            const result = await deleteBarnMember(barnMembershipInfo);

            if (result.isSuccess) {
                setError("");
                setSelectedBarn(null);
                // Update the barn memberships to ensure the deleted item is removed
                setFormattedBarns((prevBarns) => prevBarns.filter((b) => b.barn.id !== modalBarn.id));
            } else {
                setError("Failed to delete membership.");
            }
        } catch (error) {
            setError("An error occurred while deleting the membership.");
        } finally {
            setIsDeleteModalOpen(false);
            setIsLoading(false);
        }
    };

    return (
        <>
            <EntryFormSectionHeader title="Select a Barn" iconSrc={barnIcon} />

            {/* Barn Search Bar */}
            <IonRow>
                <IonCol className="p-0">
                    <SelectBarnFromAllBarns onSelect={handleBarnSelection} placeholder="Search barns..." />
                </IonCol>
            </IonRow>

            {/* List of Barns */}
            {error && (
                <IonRow className="entry-form-content-gap-top">
                    <IonCol>
                        <ErrorBanner error={error} />
                    </IonCol>
                </IonRow>
            )}

            {!isLoading && formattedBarns.length === 0 && (
                <p className="text-danger entry-form-content-gap-top">
                    You're not currently a member of any barns.
                </p>
            )}

            <IonRow className="entry-form-content-gap-y">
                <IonCol className="p-0">
                    {isLoading ? (
                        <BarnCardsSkeleton />
                    ) : (
                        <IonRadioGroup value={selectedBarn?.id || formattedBarns[0]?.id}>
                            <IonRow className="ion-align-items-stretch entry-form-content-row-gap">
                                {formattedBarns.map(({ id, barn, addressString, contactString, websiteString, barnMembership }) => (
                                    <IonCol key={id} sizeXs="12" sizeSm="4" className="py-0 px-1 d-flex">
                                        <IonCard
                                            className="rounded-3 p-0 m-0 bg-white w-100"
                                            style={{
                                                minHeight: BARN_CARD_CONTENT_MIN_HEIGHT_PX,
                                                height: "100%",
                                                display: "flex",
                                            }}>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "stretch",
                                                    width: "100%",
                                                    minHeight: BARN_CARD_CONTENT_MIN_HEIGHT_PX,
                                                    flex: 1,
                                                }}>
                                                <div
                                                    className="text-primary"
                                                    style={{
                                                        flexShrink: 0,
                                                        display: "flex",
                                                        alignItems: "flex-start",
                                                        justifyContent: "center",
                                                        padding: "14px 6px 12px 10px",
                                                    }}>
                                                    <IonRadio value={id} onClick={() => handleRadioButtonClick(barn)} />
                                                </div>
                                                <div
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        padding: "10px 12px 10px 4px",
                                                    }}>
                                                    <IonCardHeader className="p-0 pb-1">
                                                        <IonCardTitle className="px-0 font-weight-bold text-primary ion-text-start">
                                                            {barn.name}
                                                        </IonCardTitle>
                                                    </IonCardHeader>
                                                    <IonCardContent className="p-0 flex-grow-1 d-flex flex-column">
                                                        <div className="ion-text-start">
                                                            <p className="m-0">{addressString}</p>
                                                            <p className="m-0">{contactString}</p>
                                                            {websiteString !== "Website not provided" ? (
                                                                <p className="m-0">
                                                                    <a href={websiteString} target="_blank" rel="noopener noreferrer">
                                                                        {websiteString}
                                                                    </a>
                                                                </p>
                                                            ) : (
                                                                <p className="m-0">Website not provided</p>
                                                            )}
                                                        </div>
                                                        <div
                                                            className="d-flex ion-justify-content-between ion-align-items-center"
                                                            style={{ marginTop: "auto", paddingTop: "8px" }}>
                                                            {barnMembership ? 
                                                                <>
                                                                    {(barnMembership.permissionLevel === "admin") ? (
                                                                        <IonChip className="rounded-2 font-weight-bold bg-info text-white mx-0">Admin</IonChip>
                                                                    ) : (
                                                                        <IonChip className="rounded-2 font-weight-bold bg-info text-white mx-0">Member</IonChip>
                                                                    )}
                                                                </>
                                                                :
                                                                (<>
                                                                    <IonChip
                                                                        className="rounded-2 font-weight-bold bg-primary text-white mx-0"
                                                                        onClick={() => openJoinModal(barn)}>
                                                                        + Become a Member
                                                                    </IonChip>
                                                                </>)
                                                            }
                                                            <IonIcon
                                                                className="card-icon-size-2 text-danger"
                                                                icon={trash}
                                                                onClick={() => {
                                                                    if (
                                                                        barnMembership &&
                                                                        (barnMembership.permissionLevel === "member" || barnMembership.permissionLevel === "admin")
                                                                    ) {
                                                                        openDeleteModal(barn);
                                                                    } else {
                                                                        setFormattedBarns((prevBarns) => prevBarns.filter((b) => b.barn.id !== barn.id));
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                    </IonCardContent>
                                                </div>
                                            </div>
                                        </IonCard>
                                    </IonCol>
                                ))}
                            </IonRow>
                        </IonRadioGroup>
                    )}
                </IonCol>
            </IonRow>

            {/* Modal: Become a Member */}
            <BasicModal
                isOpen={isJoinModalOpen}
                onClose={closeJoinModal}
                title="Become a Member"
                isLoading={isLoading}
                errorMessage={error}
                footerButtons={[
                    {
                        text: "Cancel",
                        color: "light",
                        disabled: isLoading,
                        onClick: closeJoinModal,
                    },
                    {
                        text: "Join Barn",
                        loadingText: "Joining...",
                        color: "success",
                        disabled: isLoading,
                        onClick: addMembership,
                    },
                ]}>
                <>
                    <IonRow className="ion-justify-content-center">
                        <IonCol className="ion-text-center">{modalBarn && <h4 className="text-info font-weight-bold">Are you sure you want to join {modalBarn.name}?</h4>}</IonCol>
                    </IonRow>
                    <IonRow className="ion-justify-content-center">
                        <IonCol className="ion-text-center">
                            <p>Joining this barn will share information about yourself and your horse(s).</p>
                            <p>As a member, you'll have access to this barn's information such as trainers, owners, and riders to speed up future submissions.</p>
                        </IonCol>
                    </IonRow>
                </>
            </BasicModal>

            {/* Modal: Delete Membership */}
            <BasicModal
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                title="Remove Membership"
                isLoading={isLoading}
                errorMessage={error}
                footerButtons={[
                    {
                        text: "Cancel",
                        color: "light",
                        disabled: isLoading,
                        onClick: closeDeleteModal,
                    },
                    {
                        text: "Remove",
                        loadingText: "Removing...",
                        color: "danger",
                        disabled: isLoading || !modalBarn,
                        onClick: deleteMembership,
                    },
                ]}>
                <>
                    <IonRow className="ion-justify-content-center">
                        <IonCol className="ion-text-center">
                            {modalBarn ? (
                                <h4 className="text-info font-weight-bold">Remove Membership to {modalBarn.name}?</h4>
                            ) : (
                                <h4 className="text-danger font-weight-bold">No barn selected</h4>
                            )}
                        </IonCol>
                    </IonRow>
                    {modalBarn && (
                        <IonRow className="ion-justify-content-center">
                            <IonCol className="ion-text-center">
                                <h5 className="text-medium">Doing this will...</h5>
                                <ol className="text-left">
                                    <li>Revoke access to this barn's information such as trainers, owners, and riders.</li>
                                    <li>Unshare all information about yourself and your horse(s).</li>
                                    <li>Make future submissions slower by disabling auto-fill.</li>
                                </ol>
                            </IonCol>
                        </IonRow>
                    )}
                </>
            </BasicModal>
        </>
    );
};

export default SelectBarnSection;
